import type { ReactNode } from 'react';
import { ArrowDownAZ, Clock, Hash, RefreshCw } from 'lucide-react';
import type {
  Contact,
  ContactEmail,
  ContactListParams,
  ContactPhoneNumber,
  ContactSource,
  ContactType,
  LeadSource,
} from '@/types/contact';
import { formatDateTimeInTimeZone } from '@/lib/date-values';

export type ContactTypeFilter = 'all' | ContactType;
export type ContactSourceFilter = 'all' | ContactSource;
export type ContactMembershipFilter = 'all' | 'active' | 'inactive' | 'none';

export interface ContactSortOption {
  label: string;
  icon: ReactNode;
  sortBy: NonNullable<ContactListParams['sortBy']>;
  sortDir: NonNullable<ContactListParams['sortDir']>;
}

/**
 * Who the list is about.
 *
 * A location's service and billing people are full contacts, so without this
 * the customer list fills up with people who are only ever somebody else's
 * site contact. Derived server-side from the data — nothing is stored on the
 * contact, so it cannot go stale.
 */
export type ContactWhoFilter = 'CUSTOMER' | 'SITE_CONTACT' | 'ALL';

export const CONTACT_WHO_FILTERS: Array<{ value: ContactWhoFilter; label: string }> = [
  { value: 'CUSTOMER', label: 'Customers' },
  { value: 'SITE_CONTACT', label: 'Site contacts' },
  { value: 'ALL', label: 'Everyone' },
];

export interface ContactListFilters {
  q: string;
  /**
   * Customers by default. This list is the book of accounts — who we work for
   * — and the people standing at a site are reached through the location they
   * belong to, not by scrolling past them here. They are still contacts and
   * still findable: the Who filter offers Site contacts and Everyone, and a
   * search by name reaches them whatever it is set to.
   */
  who: ContactWhoFilter;
  type: ContactTypeFilter;
  source: ContactSourceFilter;
  membership: ContactMembershipFilter;
  /** Tenant custom status name to filter by, or 'all'. */
  customStatus: string;
  /** Tag IDs to filter by (server matches contacts with ALL of them). */
  tags: string[];
  createdFrom: string;
  createdTo: string;
  page: number;
  sort: number;
}

export const CONTACT_TYPE_FILTERS: Array<{ value: ContactTypeFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'RESIDENTIAL', label: 'Residential' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  { value: 'PROPERTY_MANAGER', label: 'Property Manager' },
];

export const CONTACT_SOURCE_FILTERS: Array<{ value: ContactSourceFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'MANUAL', label: 'Manual' },
  { value: 'LEAD', label: 'Lead' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'IMPORT', label: 'Import' },
];

export const CONTACT_MEMBERSHIP_FILTERS: Array<{
  value: ContactMembershipFilter;
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'none', label: 'None' },
];

export const CONTACT_TYPE_DOT_COLORS: Record<string, string> = {
  all: 'bg-muted-foreground/60',
  RESIDENTIAL: 'bg-blue-500',
  COMMERCIAL: 'bg-emerald-500',
  PROPERTY_MANAGER: 'bg-purple-500',
};

export const CONTACT_SORT_OPTIONS: ContactSortOption[] = [
  {
    label: 'Newest',
    icon: <Clock size={12} />,
    sortBy: 'createdAt',
    sortDir: 'desc',
  },
  {
    label: 'Recently updated',
    icon: <RefreshCw size={12} />,
    sortBy: 'updatedAt',
    sortDir: 'desc',
  },
  {
    label: 'Name A-Z',
    icon: <ArrowDownAZ size={12} />,
    sortBy: 'displayName',
    sortDir: 'asc',
  },
  {
    label: 'Contact #',
    icon: <Hash size={12} />,
    sortBy: 'contactNumber',
    sortDir: 'asc',
  },
];

export const DEFAULT_CONTACT_LIST_FILTERS: ContactListFilters = {
  q: '',
  who: 'CUSTOMER',
  type: 'all',
  source: 'all',
  membership: 'all',
  customStatus: 'all',
  tags: [],
  createdFrom: '',
  createdTo: '',
  page: 1,
  sort: 0,
};

/** Coerce a URL/search value into a clean string[] of tag IDs. */
export function coerceTagIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

export function resolveContactListFilters(search: Partial<ContactListFilters>): ContactListFilters {
  const sort =
    typeof search.sort === 'number' && Number.isFinite(search.sort)
      ? Math.min(Math.max(Math.floor(search.sort), 0), CONTACT_SORT_OPTIONS.length - 1)
      : DEFAULT_CONTACT_LIST_FILTERS.sort;

  return {
    q: typeof search.q === 'string' ? search.q : DEFAULT_CONTACT_LIST_FILTERS.q,
    who: CONTACT_WHO_FILTERS.some((filter) => filter.value === search.who)
      ? (search.who as ContactWhoFilter)
      : DEFAULT_CONTACT_LIST_FILTERS.who,
    type: CONTACT_TYPE_FILTERS.some((filter) => filter.value === search.type)
      ? (search.type as ContactTypeFilter)
      : DEFAULT_CONTACT_LIST_FILTERS.type,
    source: CONTACT_SOURCE_FILTERS.some((filter) => filter.value === search.source)
      ? (search.source as ContactSourceFilter)
      : DEFAULT_CONTACT_LIST_FILTERS.source,
    membership: CONTACT_MEMBERSHIP_FILTERS.some((filter) => filter.value === search.membership)
      ? (search.membership as ContactMembershipFilter)
      : DEFAULT_CONTACT_LIST_FILTERS.membership,
    customStatus:
      typeof search.customStatus === 'string'
        ? search.customStatus
        : DEFAULT_CONTACT_LIST_FILTERS.customStatus,
    tags: coerceTagIds(search.tags),
    createdFrom:
      typeof search.createdFrom === 'string'
        ? search.createdFrom
        : DEFAULT_CONTACT_LIST_FILTERS.createdFrom,
    createdTo:
      typeof search.createdTo === 'string'
        ? search.createdTo
        : DEFAULT_CONTACT_LIST_FILTERS.createdTo,
    page:
      typeof search.page === 'number' && search.page > 0
        ? Math.floor(search.page)
        : DEFAULT_CONTACT_LIST_FILTERS.page,
    sort,
  };
}

export function cleanContactListSearch(filters: ContactListFilters): Partial<ContactListFilters> {
  const clean: Partial<ContactListFilters> = { ...filters };
  (Object.keys(DEFAULT_CONTACT_LIST_FILTERS) as Array<keyof ContactListFilters>).forEach((key) => {
    // Arrays (tags) don't compare by `===`; drop them when empty.
    if (Array.isArray(clean[key])) {
      if ((clean[key] as unknown[]).length === 0) delete clean[key];
      return;
    }
    if (clean[key] === DEFAULT_CONTACT_LIST_FILTERS[key] || clean[key] == null) {
      delete clean[key];
    }
  });
  return clean;
}

export function buildContactListParams(
  filters: ContactListFilters,
  limit: number,
): ContactListParams {
  const sortOption = CONTACT_SORT_OPTIONS[filters.sort] ?? CONTACT_SORT_OPTIONS[0];
  const search = filters.q.trim();

  return {
    page: filters.page,
    limit,
    search: search || undefined,
    // A search from the default Contacts view must find site contacts too.
    // An explicit Site contacts filter still narrows the search.
    kind:
      filters.who === 'ALL' || (search && filters.who === DEFAULT_CONTACT_LIST_FILTERS.who)
        ? undefined
        : filters.who,
    contactType: filters.type === 'all' ? undefined : filters.type,
    source: filters.source === 'all' ? undefined : filters.source,
    membershipStatus: filters.membership === 'all' ? undefined : filters.membership,
    customStatus: filters.customStatus === 'all' ? undefined : filters.customStatus,
    tags: filters.tags.length ? filters.tags : undefined,
    createdFrom: filters.createdFrom || undefined,
    createdTo: filters.createdTo || undefined,
    sortBy: sortOption.sortBy,
    sortDir: sortOption.sortDir,
  };
}

export function countContactAdvancedFilters(filters: ContactListFilters) {
  return [
    filters.who !== DEFAULT_CONTACT_LIST_FILTERS.who,
    filters.type !== 'all',
    filters.source !== 'all',
    filters.membership !== 'all',
    filters.customStatus !== 'all',
    filters.tags.length > 0,
    !!filters.createdFrom || !!filters.createdTo,
  ].filter(Boolean).length;
}

export function getContactDisplayName(contact: Contact) {
  return (
    contact.displayName ||
    [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
    contact.companyName ||
    contact.contactNumber ||
    'Untitled contact'
  );
}

/**
 * The contact-point fields the resolvers below read. Kept structural so both
 * `Contact` and the leaner `LeadContact` (leads list) can be resolved by one
 * source of truth — a synced contact may carry the address only in
 * `primaryEmail`/`primaryPhone` or the `emails`/`phoneNumbers` arrays.
 */
export type ContactContactPoints = {
  primaryEmail?: string | null;
  emails?: ContactEmail[];
  primaryPhone?: string | null;
  phoneNumbers?: ContactPhoneNumber[];
};

export function getContactEmail(contact: ContactContactPoints) {
  return (
    contact.primaryEmail ||
    contact.emails?.find((email) => email.isPrimary)?.email ||
    contact.emails?.[0]?.email ||
    ''
  );
}

export function getContactPhone(contact: ContactContactPoints) {
  return (
    contact.primaryPhone ||
    contact.phoneNumbers?.find((phone) => phone.isPrimary)?.number ||
    contact.phoneNumbers?.[0]?.number ||
    ''
  );
}

export function formatContactType(value?: ContactType | string | null) {
  if (!value) return '-';
  return value
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatContactSource(value?: ContactSource | LeadSource | string | null) {
  if (!value) return '-';
  return value
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatMembershipStatus(contact: Contact) {
  if (contact.membershipPlanName) return contact.membershipPlanName;
  if (contact.membershipStatus) return formatContactSource(contact.membershipStatus);
  if (contact.activeMembershipId || contact.membershipId) return 'Active';
  return 'None';
}

export function formatContactDate(value?: string | null, timeZone?: string | null) {
  if (!value) return '-';
  return (
    formatDateTimeInTimeZone(value, timeZone, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) || '-'
  );
}
